<template>
  <div class="change-password-form">
    <form @submit.prevent="handleSubmit">
      <TextInput
        id="current-password"
        label="Current Password"
        v-model="inputs.currentPassword.binding"
        :error="inputs.currentPassword.error"
        type="password"
      />
      <TextInput
        id="new-password"
        label="New Password"
        v-model="inputs.newPassword.binding"
        :error="inputs.newPassword.error"
        type="password"
      />
      <TextInput
        id="confirm-password"
        label="Confirm New Password"
        v-model="inputs.confirmPassword.binding"
        :error="inputs.confirmPassword.error"
        type="password"
      />
      <div class="change-password-form__buttons">
        <Btn
          type="submit"
          text="Update Password"
          :loading="isLoading"
          variant="secondary"
          :disabled="isLoading"
        />
      </div>
    </form>
  </div>
</template>

<script>
  import { ref, computed } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import { supabase } from '@/services/supabase';
  import { useUIStore } from '@/store/uiStore';
  import { useUserStore } from '@/store/userStore';

  export default {
    name: 'ChangePasswordModal',

    components: {
      Btn,
      TextInput,
    },

    props: {
      onSubmit: {
        type: Function,
        required: true,
      },
    },

    setup(props) {
      const uiStore = useUIStore();
      const userStore = useUserStore();
      const isLoading = ref(false);

      const inputs = ref({
        currentPassword: {
          id: 'currentPassword',
          binding: '',
          error: '',
        },
        newPassword: {
          id: 'newPassword',
          binding: '',
          error: '',
        },
        confirmPassword: {
          id: 'confirmPassword',
          binding: '',
          error: '',
        },
      });

      const userEmail = computed(() => userStore.user?.email);

      const validateForm = () => {
        let isValid = true;

        // Reset errors
        inputs.value.currentPassword.error = '';
        inputs.value.newPassword.error = '';
        inputs.value.confirmPassword.error = '';

        // Validate current password
        if (!inputs.value.currentPassword.binding) {
          inputs.value.currentPassword.error = 'Current password is required';
          isValid = false;
        }

        // Validate new password
        if (!inputs.value.newPassword.binding) {
          inputs.value.newPassword.error = 'New password is required';
          isValid = false;
        } else if (inputs.value.newPassword.binding.length < 6) {
          inputs.value.newPassword.error = 'Password must be at least 6 characters';
          isValid = false;
        }

        // Validate confirm password
        if (!inputs.value.confirmPassword.binding) {
          inputs.value.confirmPassword.error = 'Please confirm your new password';
          isValid = false;
        } else if (inputs.value.newPassword.binding !== inputs.value.confirmPassword.binding) {
          inputs.value.confirmPassword.error = 'Passwords do not match';
          isValid = false;
        }

        return isValid;
      };

      const handleSubmit = async () => {
        if (!validateForm()) {
          return;
        }

        isLoading.value = true;

        try {
          // First, verify current password by re-authenticating
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: userEmail.value,
            password: inputs.value.currentPassword.binding,
          });

          if (signInError) {
            inputs.value.currentPassword.error = 'Current password is incorrect';
            isLoading.value = false;
            return;
          }

          // Update to new password
          const { error: updateError } = await supabase.auth.updateUser({
            password: inputs.value.newPassword.binding,
          });

          if (updateError) {
            throw updateError;
          }

          // Success
          uiStore.addModal(null, {
            title: 'Password Updated',
            description: 'Your password has been successfully updated.',
          });

          props.onSubmit();
        } catch (error) {
          console.error('Password change error:', error.message);
          uiStore.addModal(null, {
            title: 'Error',
            description: error.message || 'Failed to update password. Please try again.',
          });
        } finally {
          isLoading.value = false;
        }
      };

      return {
        inputs,
        handleSubmit,
        isLoading,
      };
    },
  };
</script>

<style scoped lang="scss">
  .change-password-form {
    &__buttons {
      display: flex;
      justify-content: flex-end;
      margin-top: 1.5rem;
    }
  }
</style>
